import { fetchPortalMembersByKind } from './accessManagement';
import { emailToUsername } from './emailUtils';
import { formatDisplayPersonName } from './formatPersonName';
import { formatSupabaseError } from './formatSupabaseError';

type DemActorProfile = {
  username?: string | null;
  email?: string | null;
};

/** Placeholder written when profile/session identity is missing — never intentional. */
export const DEM_ANONYMOUS_ACTOR = 'Web user';

/** Web portal user id — always email local-part (e.g. json@… → json). */
export function getDemActorId(
  profile: DemActorProfile | null | undefined,
  sessionEmail?: string | null,
): string {
  const email = profile?.email?.trim() || sessionEmail?.trim() || '';
  if (email) return emailToUsername(email);

  const username = profile?.username?.trim();
  if (username) return username;

  return DEM_ANONYMOUS_ACTOR;
}

export function isDemAnonymousActor(actor: string | null | undefined): boolean {
  return String(actor ?? '').trim().toLowerCase() === DEM_ANONYMOUS_ACTOR.toLowerCase();
}

/** Block create/export while profile/session identity is still loading. */
export function assertDemActorReady(actor: string | null | undefined): void {
  if (!isDemAnonymousActor(actor)) return;
  throw new Error(
    'Account still loading — wait a moment, then try again. Orders must not be saved as “Web user”.',
  );
}

/** Match own orders in DB (email id + legacy username if different). */
export function getDemActorIds(
  profile: DemActorProfile | null | undefined,
  sessionEmail?: string | null,
): string[] {
  const ids = new Set<string>();
  const primary = getDemActorId(profile, sessionEmail);
  if (primary && !isDemAnonymousActor(primary)) ids.add(primary);

  const legacy = profile?.username?.trim();
  if (legacy && legacy.toLowerCase() !== primary.toLowerCase()) {
    ids.add(legacy);
  }

  const sessionLocal = sessionEmail?.trim() ? emailToUsername(sessionEmail) : '';
  if (sessionLocal && sessionLocal.toLowerCase() !== primary.toLowerCase()) {
    ids.add(sessionLocal);
  }

  return [...ids];
}

function putDisplayAlias(
  map: Map<string, string>,
  rawKey: string | null | undefined,
  displayName: string,
) {
  const key = String(rawKey ?? '').trim().toLowerCase();
  if (!key || !displayName) return;

  map.set(key, displayName);

  const spaced = key.replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (spaced && spaced !== key) map.set(spaced, displayName);

  const underscored = key.replace(/\s+/g, '_');
  if (underscored && underscored !== key) map.set(underscored, displayName);
}

/** Email local-part → display name from portal_access.dem_role members. */
export async function fetchDemUserDisplayNameMap(): Promise<Map<string, string>> {
  let rows: Awaited<ReturnType<typeof fetchPortalMembersByKind>>;
  try {
    rows = await fetchPortalMembersByKind('dem');
  } catch (error) {
    throw new Error(formatSupabaseError(error));
  }

  const map = new Map<string, string>();

  for (const row of rows) {
    const email = String(row.email ?? '').trim();
    if (!email) continue;

    const actorId = emailToUsername(email);
    const displayName = formatDisplayPersonName(
      String(row.display_name ?? '').trim() || actorId,
    );
    putDisplayAlias(map, actorId, displayName);

    const legacyUsername = String(row.username ?? '').trim();
    if (legacyUsername) {
      putDisplayAlias(map, legacyUsername, displayName);
    }
  }

  return map;
}

/** Actor ids (email local-part / legacy username) whose id or display name matches search. */
export async function findDemActorIdsMatchingSearch(search: string): Promise<string[]> {
  const kw = search.trim().toLowerCase();
  if (!kw) return [];

  let rows: Awaited<ReturnType<typeof fetchPortalMembersByKind>>;
  try {
    rows = await fetchPortalMembersByKind('dem');
  } catch (error) {
    throw new Error(formatSupabaseError(error));
  }

  const ids = new Set<string>();
  for (const row of rows) {
    const email = String(row.email ?? '').trim();
    const actorId = email ? emailToUsername(email) : '';
    const legacyUsername = String(row.username ?? '').trim();
    const displayName = formatDisplayPersonName(
      String(row.display_name ?? '').trim() || actorId || legacyUsername,
    );
    const haystack = [actorId, legacyUsername, displayName, email].join(' ').toLowerCase();
    if (!haystack.includes(kw)) continue;
    if (actorId) ids.add(actorId);
    if (legacyUsername) ids.add(legacyUsername);
  }
  return [...ids];
}

export function resolveDemDisplayUser(
  raw: string | null | undefined,
  nameMap: Map<string, string>,
): string {
  if (!raw || raw === '-') return '—';
  const value = String(raw).trim();
  const isVoid = value.includes(' (Void)');
  const base = isVoid ? value.replace(' (Void)', '').trim() : value;
  const key = base.toLowerCase();
  const spacedKey = key.replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim();
  const underscoredKey = key.replace(/\s+/g, '_');
  const mapped =
    nameMap.get(key) ??
    nameMap.get(spacedKey) ??
    nameMap.get(underscoredKey) ??
    null;
  const display = formatDisplayPersonName(mapped ?? base);
  return isVoid ? `${display} (Void)` : display;
}
